/*
 * lazy-native.js: drop-in replacement for lazysizes.min.js (Speed work, 2026-10).
 *
 * lazysizes re-measured every pending element on each scroll and DOM mutation, forcing layout on a busy
 * page (~0.4-0.8s main thread in Lighthouse). This shim hands lazy loading to the browser instead
 * (loading="lazy"), and keeps the class lifecycle the theme CSS depends on:
 *   .lazyload -> .lazyloading -> .lazyloaded   (e.g. card images stay opacity:0 until .lazyloaded)
 * Supports: data-src, data-srcset, data-sizes="auto", <picture><source data-srcset>, iframes,
 * data-bgset / data-bg backgrounds, and elements added later (AJAX sections, quick view, etc).
 */
(function () {
  'use strict';
  if (window.lazySizes) return;

  var cfg = window.lazySizesConfig || {};
  var LAZY = cfg.lazyClass || 'lazyload';
  var LOADING = cfg.loadingClass || 'lazyloading';
  var LOADED = cfg.loadedClass || 'lazyloaded';

  function swapClass(el) {
    el.classList.remove(LAZY);
    el.classList.add(LOADING);
  }

  function done(el) {
    el.classList.remove(LOADING);
    el.classList.add(LOADED);
  }

  // data-sizes="auto": widths are measured for a whole batch first (reads), then applied (writes),
  // so a page full of images costs one layout instead of one forced layout per image.
  var widths = new Map();
  function measure(el) {
    if (el.getAttribute('data-sizes') !== 'auto') return;
    widths.set(el, el.offsetWidth || (el.parentNode && el.parentNode.offsetWidth) || 0);
  }
  function autoSizes(el) {
    if (el.getAttribute('data-sizes') !== 'auto') return;
    var w = widths.get(el);
    if (w) el.setAttribute('sizes', w + 'px');
  }

  function unveil(el) {
    if (!el.classList || !el.classList.contains(LAZY)) return;
    swapClass(el);

    var tag = el.nodeName;
    if (tag === 'IMG' || tag === 'IFRAME' || tag === 'VIDEO') {
      if (!el.hasAttribute('loading') && tag !== 'VIDEO') el.setAttribute('loading', 'lazy');
      if (tag === 'IMG') {
        var pic = el.parentNode && el.parentNode.nodeName === 'PICTURE' ? el.parentNode : null;
        if (pic) {
          pic.querySelectorAll('source[data-srcset]').forEach(function (s) {
            autoSizes(s);
            s.setAttribute('srcset', s.getAttribute('data-srcset'));
          });
        }
        autoSizes(el);
        if (el.complete && el.naturalWidth && !el.getAttribute('data-src') && !el.getAttribute('data-srcset')) {
          done(el);
          return;
        }
        el.addEventListener('load', function () { done(el); }, { once: true });
        el.addEventListener('error', function () { done(el); }, { once: true });
      } else {
        el.addEventListener('load', function () { done(el); }, { once: true });
      }
      var srcset = el.getAttribute('data-srcset');
      var src = el.getAttribute('data-src');
      if (srcset) el.setAttribute('srcset', srcset);
      if (src) el.setAttribute('src', src);
      if (!srcset && !src && tag === 'IMG' && el.complete) done(el);
      return;
    }

    // Background images (data-bgset uses the first candidate; data-bg is a single URL)
    var bg = el.getAttribute('data-bg') || (el.getAttribute('data-bgset') || '').split(',')[0].trim().split(' ')[0];
    if (bg) el.style.backgroundImage = 'url("' + bg + '")';
    done(el);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var list = Array.prototype.slice.call(root.querySelectorAll('.' + LAZY));
    if (root.classList && root.classList.contains(LAZY)) list.unshift(root);
    if (!list.length) return;
    // Phase 1: reads only
    for (var i = 0; i < list.length; i++) {
      measure(list[i]);
      if (list[i].nodeName === 'IMG' && list[i].parentNode && list[i].parentNode.nodeName === 'PICTURE') {
        list[i].parentNode.querySelectorAll('source[data-sizes="auto"]').forEach(measure);
      }
    }
    // Phase 2: writes only
    for (var j = 0; j < list.length; j++) unveil(list[j]);
    widths.clear();
  }

  // Batch elements added after load (AJAX-rendered sections, quick view, cart drawer, etc).
  var pending = [];
  var scheduled = false;
  function flush() {
    scheduled = false;
    var nodes = pending;
    pending = [];
    for (var i = 0; i < nodes.length; i++) if (nodes[i].isConnected) scan(nodes[i]);
  }
  function start() {
    scan(document);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) if (added[j].nodeType === 1) pending.push(added[j]);
      }
      if (pending.length && !scheduled) {
        scheduled = true;
        (window.requestAnimationFrame || setTimeout)(flush);
      }
    }).observe(document.documentElement, { childList: true, subtree: true }); // (no class watching: nothing adds .lazyload later, and sliders/marquees change classes constantly)
  }

  // Minimal API surface some scripts probe for.
  window.lazySizes = {
    cfg: cfg,
    loader: { unveil: unveil, checkElems: function () { scan(document); } },
    autoSizer: { checkElems: function () {} },
    native: true,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
